import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import HistoryRow from 'Components/HistoryRow';
import colors from '../../../../../config/theme/colors';
import { exists } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { existsAndIsNotNull } from '../../../utils';
import { rowData, TPulmonaryRowDataKey } from '../../Form/Pulmonary/rowData';
import { ConditionalCollapse } from '.';

// Extracts only the properties starting with '_' (e.g., _id, _type, _ownerOrg)
const mapDocumentToControlProps = (d: TPulmonaryWithPreviousDates): IControlProps<'pulmonary'> => {
  const controlProps: Record<string, unknown> & IControlProps<'pulmonary'> = {
    _cdate: 1,
    _cid: '',
    _editing: false,
    _id: '',
    _type: 'pulmonary',
    _docCreateDate: 1,
    _creator: '',
    _ownerOrg: '',
    _lockedFor: null,
    _source: '',
    _pastOwners: {},
  };

  for (const [key, value] of Object.entries(d)) {
    if (key.startsWith('_')) {
      controlProps[key] = value;
    }
  }

  return controlProps;
};

export const History = ({ d, startEdit, rows, index }: IHistory): React.JSX.Element => {
  const documentControlProps = React.useMemo(() => mapDocumentToControlProps(d), [d]);
  return (
    <React.Fragment>
      <HistoryRow
        headerText={formatPartialDate(d.date)}
        rowButton={startEdit && <HistoryRowControls document={documentControlProps} startEdit={startEdit} />}
        controlsMargin={false}
      >
        <ConditionalCollapse condition={index > 0 && rows.length > 1} rows={rows}>
          <React.Fragment>
            {rows.map(row => {
              const thisRow = d[row];

              const rowValue = existsAndIsNotNull(thisRow)
                ? thisRow && typeof thisRow === 'object'
                  ? thisRow.value
                  : thisRow
                : undefined;
              const rowDate = typeof thisRow === 'object' ? thisRow?.date : undefined;
              return (
                <React.Fragment key={row}>
                  <HistoryRowVerticalItem
                    header={<FormattedMessage id={`pulmonary.${row}`} />}
                    value={
                      <>
                        {exists(rowValue) ? (
                          rowData[row]['optionFormat'] ? (
                            <>
                              <FormattedMessage id={`pulmonary.opts.${rowData[row]['optionFormat']}.${rowValue}`} />
                            </>
                          ) : (
                            rowValue
                          )
                        ) : (
                          '-'
                        )}
                        <div style={{ fontSize: '1.2rem', color: colors.darkGray }}>
                          {rowDate ? formatPartialDate(rowDate) : ''}
                        </div>
                      </>
                    }
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        </ConditionalCollapse>
      </HistoryRow>
    </React.Fragment>
  );
};

type WithPreviousDates<T> = {
  [K in keyof T]: { value: T[K]; date: PartialDate };
};

export type TPulmonaryWithPreviousDates =
  | IDocumentWithTypeV2<'pulmonary'>
  | (WithPreviousDates<IDocumentWithTypeV2<'pulmonary'>> & {
      date: PartialDate;
    } & IControlProps<'pulmonary'>);

interface IHistory {
  d: TPulmonaryWithPreviousDates;
  startEdit?: StartEdit;
  rows: Array<TPulmonaryRowDataKey>;
  index: number;
}
